/**
 * Per-component getter memoization (Phase 1, FRONTEND_REFACTOR_PROMPT.md).
 *
 * Angular's default change detection re-evaluates template-bound getters on every tick;
 * getters that filter/sort/group arrays therefore redo O(n) work dozens of times per
 * second. Wrapping the body in `memo.get(...)` makes the tick cost a few reference
 * comparisons: the function only re-runs when one of its declared dependencies changes
 * (reference equality — so state updates must REPLACE arrays/objects, not mutate them,
 * which is already the dominant pattern in this codebase).
 *
 *   private memo = new Memo();
 *   get filtered(): Row[] {
 *     return this.memo.get('filtered', [this.rows, this.search, this.status],
 *       () => this.rows.filter(...));
 *   }
 *
 * This is the stepping stone to signals + computed(); call sites keep their names so
 * a later signal migration is mechanical.
 */
export class Memo {
  private cache = new Map<string, { deps: unknown[]; value: unknown }>();

  get<T>(key: string, deps: unknown[], compute: () => T): T {
    const hit = this.cache.get(key);
    if (hit && hit.deps.length === deps.length && hit.deps.every((d, i) => d === deps[i])) {
      return hit.value as T;
    }
    const value = compute();
    this.cache.set(key, { deps: [...deps], value });
    return value;
  }

  /** Drop one key (or everything) — for the rare mutation done in place. */
  invalidate(key?: string): void {
    if (key === undefined) this.cache.clear();
    else this.cache.delete(key);
  }
}
